import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Modal, Spinner } from '@companycam/slab-web';
import { useModal } from 'components/shared/hooks';
import { translate } from 'javascripts/translations/i18n';

import {
  useGetMapEmbeddingConfigs,
  useCreateMapEmbeddingConfig,
  useUpdateMapEmbeddingConfig,
} from 'data/v3/mapEmbeddingConfigs';
import BrandColorPicker from './BrandColorPicker';

import { CopyEmbedCode } from './CopyEmbedCode';
import { MapPreview } from '../MapPreview';
import { DEFAULT_PIN_COLOR } from './constants';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xl);
`;

const MapContainer = styled.div`
  height: 250px;
  border-radius: var(--cc_size_border_radius_l);
`;

const MapWizardModal = () => {
  const { hideModal } = useModal();
  const {
    data: mapEmbeddingConfigs,
    isSuccess,
    isLoading: isMapEmbeddingConfigLoading,
  } = useGetMapEmbeddingConfigs();
  const { mutate, isPending } = useCreateMapEmbeddingConfig();
  const { mutate: updateMapEmbeddingConfig } = useUpdateMapEmbeddingConfig();

  const isLoading = isMapEmbeddingConfigLoading || isPending;

  useEffect(() => {
    if (isSuccess && mapEmbeddingConfigs?.data.length === 0) {
      mutate({ brand_color: `#${DEFAULT_PIN_COLOR}` });
    }
  }, [isSuccess, mapEmbeddingConfigs?.data.length, mutate]);
  const mapEmbedConfig = mapEmbeddingConfigs?.data[0];
  const brandColor = mapEmbedConfig?.brandColor;

  const handleUpdateBrandColor = (updatedBrandColor: string) => {
    updateMapEmbeddingConfig({
      mapEmbeddingConfigId: mapEmbedConfig.id,
      params: {
        brand_color: updatedBrandColor,
      },
    });
  };

  return (
    <Modal
      title={translate('mapEmbedWizard.embedModalTitle')}
      bodyStyle={{
        paddingLeft: 'var(--cc_size_spacing_xl)',
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingBottom: 'var(--cc_size_spacing_xl)',
        paddingTop: 'var(--cc_size_spacing_l)',
      }}
      headerStyle={{
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingLeft: 'var(--cc_size_spacing_xl)',
        paddingTop: 'var(--cc_size_spacing_xl)',
        paddingBottom: 0,
        borderBottomWidth: 0,
      }}
      footerComponent={null}
      isOpen
      size="small"
      onClose={hideModal}
    >
      {isLoading ? (
        <LoadingContainer>
          <Spinner
            size="medium"
            accessibilityLabel={translate('common.loading')}
            data-testid="loading-spinner"
          />
        </LoadingContainer>
      ) : (
        <ContentContainer>
          <MapContainer>
            <MapPreview
              brandColor={mapEmbedConfig?.brandColor}
              markerDisplayLimit={1}
              showControls={false}
            />
          </MapContainer>
          <BrandColorPicker
            brandColor={brandColor}
            onColorPicked={handleUpdateBrandColor}
          />
          <CopyEmbedCode publicIdentifier={mapEmbedConfig?.publicIdentifier} />
        </ContentContainer>
      )}
    </Modal>
  );
};

export default MapWizardModal;
